import { Cancel } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  SxProps,
  Theme,
  alpha,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import React from "react";
import Button from "../../common/Button";
import { tokens } from "../../../theme";
import { SalesDetailsDTO } from "../../../redux/slice/saleDetails/SaleDetailsSlice";
import { EXCEL_SALES_V3_LABELS } from "../../../constants/excelFieldKeys";

// ---- Props ----
interface ExcelDetailsPopupProps {
  open: boolean;
  onClose: () => void;
  rows?: SalesDetailsDTO[]; // pass API data here
  columns?: (keyof SalesDetailsDTO)[]; // choose which DB columns to show
  onOpenDocument?: (row: SalesDetailsDTO) => void; // optional handler for "Open" click
}

// ---- Transition ----
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

// ---- Labels for headers ----
const LABELS: Partial<Record<keyof SalesDetailsDTO, string>> = {
  mfg: "MFR",
  mfgConfirmNum: "MFG Confirm #",
  invoiceNum: "Invoice #*",
  trackingNum: "Tracking #",
  v3OrderId: "V3 Order ID #*",
  serialNumber: "Serial Number*",
  orderDate: "Order Date",
  orderType: "Order Type",
  orderMonthYear: "Order MO/YR",
  accountPractice: "Account ",
  product: "Product*",
  prodSize: "Prod Size",
  totalUnitsSqcm: "Total Units/sqcm",
  prodQty: "Prod Qty",
  cogsPu: "COGs P/U",
  cogsSalesOrig: "COGs/Sales Orig*",
  cogsSalesPmt: "COGs/Sales Pre Adj",
  provider: "Provider",
  dos: "Date Of Service ",
  pt: "PT",
  acctInvoiceStatus: "Invoice Pay Status*",
  totalCommPercent: "Total Service Fee %",
  totalComm: "Total Service Fee",
  payStatusToV3: EXCEL_SALES_V3_LABELS.PAY_STATUS_TO_V3,
  v3NetComm: EXCEL_SALES_V3_LABELS.NET_SERVICE_FEE,
  agency: "Agency",
  accountRep: "Account Rep",
  agencyPercent: "Agency %",
  agencyComm: "Agency Service Fee $",
  override: "Override 1st",
  overridePercent: "Override 1st %",
  overrideComm: "Override 1st Service Fee $",
  agencyPayStatus: "Agency Pay Status",
  ovrAePayStatus: "OVR Pay Status 1st"
};

// ---- Default columns to display ----
const DEFAULT_COLUMNS: (keyof SalesDetailsDTO)[] = [
  "mfg",
  "mfgConfirmNum",
  "invoiceNum",
  "trackingNum",
  "v3OrderId",
  "serialNumber",
  "orderDate",
  "orderType",
  "orderMonthYear",
  "accountPractice",
  "product",
  "prodSize",
  "totalUnitsSqcm",
  "prodQty",
  "cogsPu",
  "cogsSalesOrig",
  "cogsSalesPmt",
  "provider",
  "dos",
  "pt",
  "acctInvoiceStatus",
  "totalCommPercent",
  "totalComm",
  "payStatusToV3",
  "v3NetComm",
  "agency",
  "accountRep",
  "agencyPercent",
  "agencyComm",
  "override",
  "overridePercent",
  "overrideComm",
  "agencyPayStatus",
  "ovrAePayStatus"
];

// ---- Helpers ----
const toColLetters = (i: number) => {
  // 0 -> A, 1 -> B, ... 25 -> Z, 26 -> AA, ...
  let n = i + 1;
  let s = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

const fmtNumber = (val: any, digits = 2) => {
  if (val === null || val === undefined || val === "") return "";
  const num = Number(val);
  if (Number.isNaN(num)) return String(val);
  return num.toFixed(digits);
};

const fmtDate = (iso?: string | null) => {
  if (!iso) return "";
  // Expecting "YYYY-MM-DD" or ISO string; keep simple YYYY-MM-DD
  return iso.substring(0, 10);
};

// ---- Component ----
const ExcelDetailsPopup: React.FC<ExcelDetailsPopupProps> = ({
  open,
  onClose,
  rows,
  columns,
  onOpenDocument,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const data = rows && rows.length ? rows : [];
  const visible = (
    columns && columns.length ? columns : DEFAULT_COLUMNS
  ).filter(
    (k) => LABELS[k] // only show ones we have a label for
  );

  // Excel-ish styles
  const sheetSx: SxProps<Theme> = {
    fontFamily: `"Segoe UI", Tahoma, Arial, sans-serif`,
    fontSize: 13,
    border: 1, // 1px solid
    borderColor: "divider",
    borderRadius: 1,
    overflow: "auto",
    bgcolor: "background.paper",
  };

  const headerLetterSx: SxProps<Theme> = (theme) => ({
    bgcolor: colors.success[500], // Excel-ish tint
    fontWeight: 600,
    textAlign: "center",
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    py: 0.5,
    whiteSpace: "nowrap",
  });

  const baseCell = (theme: Theme) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    px: 1,
    py: 0.5,
    height: 32,
    whiteSpace: "nowrap" as const,
  });

  const rowNumberSx: SxProps<Theme> = (theme) => ({
    width: 36,
    minWidth: 36,
    bgcolor: '#252525',
    textAlign: "right",
    color: theme.palette.text.secondary,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    pr: 1,
    fontWeight: 600,
    position: "sticky",
    left: 0,
    zIndex: 1,
  });

  const cellSx: SxProps<Theme> = (theme) => baseCell(theme);

  const headerCellSx: SxProps<Theme> = (theme) => ({
    ...baseCell(theme),
    bgcolor: alpha(theme.palette.success.light, 0.35),
    fontWeight: 600,
    textAlign: "left",
    position: "sticky",
    top: 32, // below the letters row
    zIndex: 1,
  });

  const COLUMN_STYLE: Partial<
    Record<keyof SalesDetailsDTO, { bgcolor: string; color: string }>
  > = {
    totalCommPercent: { bgcolor: colors.success[100], color: colors.grey[900] },
    totalComm: { bgcolor: colors.success[100], color: colors.grey[900] },
    payStatusToV3: { bgcolor: colors.success[100], color: colors.grey[900] },
    v3NetComm: { bgcolor: colors.success[100], color: colors.grey[900] },
    agency: { bgcolor: colors.success[100], color: colors.grey[900] },
    accountRep: { bgcolor: colors.success[100], color: colors.grey[900] },
    agencyPercent: { bgcolor: colors.success[100], color: colors.grey[900] },
    agencyComm: { bgcolor: colors.success[100], color: colors.grey[900] },
    override: { bgcolor: colors.success[100], color: colors.info[300] },
    overridePercent: { bgcolor: colors.success[100], color: colors.info[300] },
    overrideComm: { bgcolor: colors.success[100], color: colors.info[300] },
    agencyPayStatus: { bgcolor: colors.success[100], color: colors.info[300] },
    ovrAePayStatus: { bgcolor: colors.success[100], color: colors.info[300] },
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-describedby="excel-details"
      onClick={(e) => e.stopPropagation()}
      fullWidth
      maxWidth="xl"
    >
      <Box sx={{ p: 1, width: "100%", maxWidth: "100%" }}>
        <Box sx={sheetSx}>
          <Table
            size="small"
            sx={{
              borderCollapse: "separate",
              borderSpacing: 0,
              minWidth: 700,
            }}
          >
            <TableHead>
              {/* Column letters */}
              <TableRow>
                {visible.map((_, i) => (
                  <TableCell key={`letter-${i}`} sx={headerLetterSx}>
                    {toColLetters(i)}
                  </TableCell>
                ))}
              </TableRow>
              {/* Field labels */}
              <TableRow>
                {visible.map((k) => (
                  <TableCell
                    key={`label-${String(k)}`}
                    sx={(theme) => {
                      const style = COLUMN_STYLE[k];
                      const bg = style?.bgcolor ?? alpha(theme.palette.success.light, 0.35);
                      const color = style?.color ?? theme.palette.text.primary;
                      return {
                        ...headerCellSx(theme),
                        bgcolor: bg,
                        color: color,
                      };
                    }}
                  >
                    {LABELS[k]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data.slice(0, 2).map((row, idx) => (
                <TableRow key={idx}>

                  {visible.map((k) => {
                    const raw = row[k];
                    let content: React.ReactNode = "";

                    if (k === "orderDate") {
                      content = fmtDate(String(raw || ""));
                    } else if (
                      k === "totalComm" ||
                      k === "v3NetComm" ||
                      k === "totalUnitsSqcm"
                    ) {
                      content = fmtNumber(raw, 2);
                    } else if (
                      k === "agencyPercent" ||
                      k === "overridePercent" ||
                      k === "totalCommPercent"
                    ) {
                      const numValue = parseFloat(String(raw || "0"));
                      if (isNaN(numValue)) {
                        content = "";
                      } else {
                        const percentage = numValue * 100;
                        const rounded = Math.round(percentage * 10) / 10;
                        const formatted = rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
                        content = `${formatted}%`;
                      }
                    }
                     else if (k === "invoiceNum") {
                      content = (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>{raw || ""}</span>
                        </Box>
                      );
                    } else {
                      content = raw as any as React.ReactNode;
                    }

                    return (
                      <TableCell
                        key={`${idx}-${String(k)}`}
                        sx={(theme) => {
                          const style = COLUMN_STYLE[k];
                          // const bg = style?.bgcolor ?? "inherit";
                          // const color = style?.color ?? theme.palette.text.primary;
                          return {
                            ...cellSx(theme),
                            // bgcolor: bg,
                            color: colors.grey[500],
                          };
                        }}
                      >
                        {content}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <DialogActions>
        <Button color="error" startIcon={<Cancel />} onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExcelDetailsPopup;